import type { QueryClient } from '@tanstack/react-query';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { queries } from '../../api/queries.js';
import type { Broker, BrokersResponse } from '../../api/types.js';
import { useMe } from '../auth/useMe.js';

/**
 * Which broker the tool is pointed at.
 *
 * Remembered per browser rather than held on the server. It used to be part of a login: you
 * picked a broker, the backend opened a connection with your credentials, and that connection
 * WAS the session. Nothing about that survives — every connection now carries this service's
 * own identity — so the choice is a preference, and a preference belongs where the person made
 * it. It travels to the API as `?broker=`, which also makes it visible in the network tab and
 * reproducible with `curl`.
 *
 * `null` means "whatever this instance was deployed with", so the stored value never outlives
 * the broker it names.
 */
const STORAGE_KEY = 'event-explorer.broker';

const selectedBrokerAtom = atomWithStorage<string | null>(STORAGE_KEY, null);

/**
 * The stored choice, read without React.
 *
 * Route loaders need the broker before any component mounts, and they cannot call a hook. One
 * reader and one resolver (below) is what keeps a loader and the picker from disagreeing about
 * which broker a page is showing. `atomWithStorage` stores JSON, so this parses it.
 */
function storedBroker(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : null;
  } catch {
    // A private window, or site data blocked. Falling back to the configured broker is the
    // right answer and is not worth an error.
    return null;
  }
}

/**
 * The selected broker, resolved against the list the server offers.
 *
 * A stored URL that is no longer offered is discarded rather than sent: a review environment
 * lives for days and the choice outlives it in localStorage. Sending it anyway would answer
 * every request with UNKNOWN_BROKER and read as a broken tool rather than a gone environment.
 */
export function resolveBroker(brokers: Broker[], defaultBroker: string, stored: string | null): Broker {
  const known = stored === null ? undefined : brokers.find((candidate) => candidate.url === stored);
  const configured = brokers.find((candidate) => candidate.url === defaultBroker);
  return (
    known ??
    configured ??
    brokers[0] ?? { name: defaultBroker, namespace: '', url: defaultBroker, configured: true }
  );
}

export interface BrokerSelection {
  /** The URL to send with requests. Always one this instance offers. */
  url: string;
  broker: Broker;
  brokers: Broker[];
  /** How the list came about, so the picker can say when discovery is blind rather than empty. */
  status: BrokersResponse['status'];
  detail: string | undefined;
  select: (url: string) => void;
}

/**
 * Forgets the stored choice, so the configured broker is used again.
 *
 * The selection lives in localStorage, so a broker that cannot serve this instance survives a
 * reload and keeps every page failing. This is the way out, offered on the error screen.
 */
export function useResetBroker(): () => void {
  const setStored = useSetAtom(selectedBrokerAtom);
  return () => setStored(null);
}

export function useBrokerSelection(): BrokerSelection {
  const me = useMe();
  const { data: list } = useSuspenseQuery(queries.brokers());
  const [stored, setStored] = useAtom(selectedBrokerAtom);

  const broker = resolveBroker(list.brokers, me.defaultBroker, stored);
  return {
    url: broker.url,
    broker,
    brokers: list.brokers,
    status: list.status,
    detail: list.detail,
    select: setStored,
  };
}

/**
 * The same answer for a route loader, which runs before any component and has no hooks.
 *
 * Both queries are in the cache by the time a page loads — `_app` ensures the identity and the
 * shell reads the broker list — so this is a cache read in the normal case.
 */
export async function loadBroker(queryClient: QueryClient): Promise<string> {
  const [me, list] = await Promise.all([
    queryClient.ensureQueryData(queries.me()),
    queryClient.ensureQueryData(queries.brokers()),
  ]);
  // Unreachable through the router: `_app.beforeLoad` aborts the match before a child loader
  // runs when nobody is signed in.
  const defaultBroker = me.ok ? me.me.defaultBroker : '';
  return resolveBroker(list.brokers, defaultBroker, storedBroker()).url;
}
